import { toast } from "react-hot-toast";
import { useGlobalContext } from "../GlobalContext";

export default function useGigs() {
  const { api, AdminId } = useGlobalContext();

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "01" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const getResponseData = (data) => {
    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.response)) {
      return data.response;
    }

    return [];
  };

  const formatDateTimeForApi = (dateTimeValue) => {
    if (!dateTimeValue) return "";

    return String(dateTimeValue).trim().replace("T", " ").slice(0, 16);
  };

  // ---------- GET ALL DRIVER SLOTS ----------

  const fetchSlotsApi = async (
    date,
    setSlotsData,
    setFilteredData,
    setLoading,
  ) => {
    setLoading?.(true);

    try {
      const payload = {
        userId: "",
      };

      const res = await api.post("/api/Admin/v1.0/getAllDriverSlots", payload);

      if (res.data?.responseCode === "00") {
        const teamGroups = getResponseData(res.data);

        const normalizedSlots = teamGroups.flatMap((teamGroup) => {
          const teamId = String(teamGroup?.team_id || "");

          const slots = Array.isArray(teamGroup?.slots) ? teamGroup.slots : [];

          return slots.map((slot) => ({
            id: String(slot?.pk_slot_id || ""),
            slotId: String(slot?.pk_slot_id || ""),

            teamId,
            team_id: teamId,

            fromDate: String(slot?.from_date || ""),
            from_date: String(slot?.from_date || ""),

            toDate: String(slot?.to_date || ""),
            to_date: String(slot?.to_date || ""),

            maximumDrivers: Number(slot?.maximum_drivers || 0),
            maximum_drivers: Number(slot?.maximum_drivers || 0),

            currentDrivers: Number(slot?.current_drivers || 0),
            current_drivers: Number(slot?.current_drivers || 0),

            category: String(slot?.category || ""),

            slotStatus: Number(slot?.slot_status || 0),
            slot_status: Number(slot?.slot_status || 0),

            isActive: Number(slot?.slot_status) === 1,

            raw: slot,
          }));
        });

        const selectedDate = String(date || "").slice(0, 10);

        const filteredSlots = selectedDate
          ? normalizedSlots.filter((slot) => {
              const slotDate = String(slot?.fromDate || "").slice(0, 10);

              return slotDate === selectedDate;
            })
          : normalizedSlots;

        setSlotsData?.(normalizedSlots);
        setFilteredData?.(filteredSlots);

        return filteredSlots;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch driver slots",
        );
      }

      setSlotsData?.([]);
      setFilteredData?.([]);

      return [];
    } catch (error) {
      console.error("fetchSlotsApi error:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Error fetching driver slots",
      );

      setSlotsData?.([]);
      setFilteredData?.([]);

      return [];
    } finally {
      setLoading?.(false);
    }
  };
  // ---------- GET ALL DRIVER SLOTS ----------

const fetchSlotsApiV2 = async (
  date,
  teamId,
  setSlotsData,
  setFilteredData,
  setLoading,
) => {
  setLoading?.(true);

  try {
    const payload = {
      _slotDate: date,
      _teamId:teamId
    };

    const res = await api.post(
      "/api/Slot/v3.0/getSlotAvailability",
      payload
    );

    if (res.data?.responseCode === "00") {
      const responseData = Array.isArray(res.data?.response)
        ? res.data.response
        : [];

      const selectedDate = String(date || "").slice(0, 10);

      const normalizedSlots = responseData.flatMap((config) => {
        const slotAvailability = Array.isArray(config?.slotAvailability)
          ? config.slotAvailability
          : [];

        return slotAvailability.map((slot) => ({
          // Slot ID
          id: String(slot?.slotId || ""),
          slotId: String(slot?.slotId || ""),

          // V2 config information
          configId: config?.configId,
          windowName: config?.windowName || "",

          // Date
          slotDate: String(config?.slotDate || ""),
          fromDate: String(slot?.slotStartTime || ""),
          from_date: String(slot?.slotStartTime || ""),

          toDate: String(slot?.slotEndTime || ""),
          to_date: String(slot?.slotEndTime || ""),

          // Capacity
          maximumDrivers: Number(config?.maxCapacity || 0),
          maximum_drivers: Number(config?.maxCapacity || 0),

          currentDrivers: Number(slot?.slotAssignedCount || 0),
          current_drivers: Number(slot?.slotAssignedCount || 0),

          remainingCapacity: Number(config?.remainingCapacity || 0),

          // Slot information
          slotIndex: Number(slot?.slotIndex || 0),
          slotAssignedCount: Number(slot?.slotAssignedCount || 0),

          // V2 doesn't have category/team/status
          category: config?.windowName || "",
          teamId: "",
          team_id: "",

          slotStatus: 1,
          slot_status: 1,
          isActive: true,

          // Keep original API object
          raw: {
            ...slot,
            configId: config?.configId,
            windowName: config?.windowName,
            maxCapacity: config?.maxCapacity,
            usedCapacity: config?.usedCapacity,
            remainingCapacity: config?.remainingCapacity,
            slotDate: config?.slotDate,
          },
        }));
      });

      const filteredSlots = selectedDate
        ? normalizedSlots.filter((slot) => {
            const slotDate = String(
              slot?.fromDate || ""
            ).slice(0, 10);

            return slotDate === selectedDate;
          })
        : normalizedSlots;

      setSlotsData?.(normalizedSlots);
      setFilteredData?.(filteredSlots);

      return filteredSlots;
    }

    if (shouldShowLocalError(res.data)) {
      toast.error(
        res.data?.responseMessage ||
          "Failed to fetch driver slots"
      );
    }

    setSlotsData?.([]);
    setFilteredData?.([]);

    return [];
  } catch (error) {
    console.error("fetchSlotsApiV2 error:", error);

    toast.error(
      error?.response?.data?.responseMessage ||
        "Error fetching driver slots"
    );

    setSlotsData?.([]);
    setFilteredData?.([]);

    return [];
  } finally {
    setLoading?.(false);
  }
};

  // ---------- CREATE DRIVER SLOT ----------

  const createSlotApi = async (form, setLoading, onSuccess) => {
    if (!form) {
      toast.error("Invalid slot data");
      return false;
    }

    const payload = {
      adminId: String(AdminId || ""),
      teamId: String(form.teamId || ""),
      fromDate: formatDateTimeForApi(form.fromDate),
      toDate: formatDateTimeForApi(form.toDate),
      maximumDrivers: String(form.maximumDrivers || ""),
      category: String(form.category || ""),
    };

    if (!payload.adminId) {
      toast.error("Admin ID is required");
      return false;
    }

    if (!payload.teamId) {
      toast.error("Team is required");
      return false;
    }

    if (!payload.fromDate) {
      toast.error("From date and time is required");
      return false;
    }

    if (!payload.toDate) {
      toast.error("To date and time is required");
      return false;
    }

    const maximumDrivers = Number(payload.maximumDrivers);

    if (
      !payload.maximumDrivers ||
      !Number.isInteger(maximumDrivers) ||
      maximumDrivers <= 0
    ) {
      toast.error("Maximum drivers must be a whole number greater than 0");
      return false;
    }

    if (!payload.category) {
      toast.error("Category is required");
      return false;
    }

    const fromDateTime = new Date(form.fromDate);
    const toDateTime = new Date(form.toDate);

    if (
      Number.isNaN(fromDateTime.getTime()) ||
      Number.isNaN(toDateTime.getTime())
    ) {
      toast.error("Please select valid date and time");
      return false;
    }

    const now = new Date();

    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    const fromDateStart = new Date(
      fromDateTime.getFullYear(),
      fromDateTime.getMonth(),
      fromDateTime.getDate(),
    );

    const toDateStart = new Date(
      toDateTime.getFullYear(),
      toDateTime.getMonth(),
      toDateTime.getDate(),
    );

    if (fromDateStart < todayStart || toDateStart < todayStart) {
      toast.error("Past date slots cannot be created.");
      return false;
    }

    if (fromDateStart.getTime() !== toDateStart.getTime()) {
      toast.error("From Date and To Date must be the same day.");
      return false;
    }

    if (toDateTime <= fromDateTime) {
      toast.error("End Time must be later than Start Time.");
      return false;
    }

    const isToday = fromDateStart.getTime() === todayStart.getTime();

    if (isToday) {
      const minimumStartTime = new Date();

      minimumStartTime.setMinutes(minimumStartTime.getMinutes() + 10);

      if (fromDateTime <= minimumStartTime) {
        toast.error(
          "For today's slot, start time must be at least 10 minutes later than the current time.",
        );
        return false;
      }
    }

    try {
      setLoading?.(true);

      console.log("CREATE DRIVER SLOT PAYLOAD:", payload);

      const res = await api.post("/api/Admin/v1.0/createDriverSlot", payload);

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Driver slot created successfully",
        );

        if (typeof onSuccess === "function") {
          await onSuccess(res.data);
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to create driver slot",
        );
      }

      return false;
    } catch (error) {
      console.error("createSlotApi error:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Error creating driver slot",
      );

      return false;
    } finally {
      setLoading?.(false);
    }
  };
  // ---------- CREATE DRIVER SLOT V2 ----------
const createSlotApiV2 = async (date,teamId, setLoading, onSuccess) => {
  if (!date) {
    toast.error("Please select a date");
    return false;
  }

  const payload = {
    date,
    teamId,
  };

  try {
    setLoading?.(true);

    const res = await api.post(
      "/api/Slot/v3.0/createSlot",
      payload
    );

    if (res.data?.responseCode === "00") {
      toast.success(
        res.data?.responseMessage || "Driver slot created successfully"
      );

      if (typeof onSuccess === "function") {
        await onSuccess(res.data);
      }

      return true;
    }

    if (shouldShowLocalError(res.data)) {
      toast.error(
        res.data?.responseMessage || "Failed to create driver slot"
      );
    }

    return false;
  } catch (error) {
    console.error("createSlotApiV2 error:", error);

    toast.error(
      error?.response?.data?.responseMessage ||
        "Error creating driver slot"
    );

    return false;
  } finally {
    setLoading?.(false);
  }
};
 // ---------- Assign DRIVER SLOT ----------
const assignDriverToSlotApiV2 = async (slotId, driverId, setLoading) => {
  setLoading?.(true);

  try {
    const payload = {
      slotId: String(slotId),
      driverId: String(driverId),
    };

    const res = await api.post(
      "/api/Slot/v1.0/assignDriverToSlot",
      payload
    );

    const responseData = res?.data;

    if (responseData?.responseCode === "00") {
      toast.success(
        responseData?.responseMessage ||
          "Driver assigned to slot successfully"
      );

      return true;
    }

    if (shouldShowLocalError(responseData)) {
      toast.error(
        responseData?.responseMessage ||
          "Failed to assign driver to slot"
      );
    }

    return false;
  } catch (error) {
    console.error("assignDriverToSlotApiV2 error:", error);

    toast.error(
      error?.response?.data?.responseMessage ||
        "Error assigning driver to slot"
    );

    return false;
  } finally {
    setLoading?.(false);
  }
};
  // ---------- DELETE DRIVER SLOT ----------

  const deleteSlotApi = async (slotId, onSuccess, setLoading) => {
    if (!slotId) {
      toast.error("Invalid slot ID");
      return false;
    }

    const payload = {
      adminId: String(AdminId || ""),
      slotId: String(slotId),
    };

    try {
      setLoading?.(true);

      const res = await api.post("/api/Admin/v1.0/deleteDriverSlot", payload);

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage || "Driver slot deleted successfully",
        );

        if (typeof onSuccess === "function") {
          await onSuccess(res.data);
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to delete driver slot",
        );
      }

      return false;
    } catch (error) {
      console.error("deleteSlotApi error:", error);

      toast.error(
        error?.response?.data?.responseMessage || "Error deleting driver slot",
      );

      return false;
    } finally {
      setLoading?.(false);
    }
  };

  // ---------- UPDATE DRIVER SLOT STATUS ----------

  const updateDriverSlotStatusApi = async (
    slotId,
    status,
    onSuccess,
    setLoading,
  ) => {
    if (!slotId) {
      toast.error("Invalid slot ID");
      return false;
    }

    if (
      status === undefined ||
      status === null ||
      String(status).trim() === ""
    ) {
      toast.error("Status is required");
      return false;
    }

    const payload = {
      adminId: String(AdminId || ""),
      slotId: String(slotId),
      status: String(status),
    };

    try {
      setLoading?.(true);

      const res = await api.post(
        "/api/Admin/v1.0/updateDriverSlotStatus",
        payload,
      );

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage ||
            "Driver slot status updated successfully",
        );

        if (typeof onSuccess === "function") {
          await onSuccess(res.data);
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to update driver slot status",
        );
      }

      return false;
    } catch (error) {
      console.error("updateDriverSlotStatusApi error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Error updating driver slot status",
      );

      return false;
    } finally {
      setLoading?.(false);
    }
  };

  // ---------- GET BOOKED DRIVERS ----------

  const fetchBookedDriversApi = async (
    slotId,
    setDriverList,
    setDriverListLoading,
  ) => {
    if (!slotId) {
      toast.error("Invalid slot ID");
      setDriverList?.([]);
      return [];
    }

    setDriverListLoading?.(true);

    try {
      const res = await api.post("/api/Driver/v1.0/getSlotBookedDriverList", {
        slotId: String(slotId),
        adminId: String(AdminId || ""),
      });

      if (res.data?.responseCode === "00") {
        const list = getResponseData(res.data);

        setDriverList?.(list);

        return list;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage || "Failed to fetch booked drivers",
        );
      }

      setDriverList?.([]);

      return [];
    } catch (error) {
      console.error("fetchBookedDriversApi error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Error fetching booked driver list",
      );

      setDriverList?.([]);

      return [];
    } finally {
      setDriverListLoading?.(false);
    }
  };

    // ---------- INSERT TIME WINDOW CONFIG ----------

  const insertTimeWindowConfigApi = async (
    form,
    setLoading,
    onSuccess,
  ) => {
    if (!form) {
      toast.error("Invalid time window configuration");
      return false;
    }

const payload = {
  adminId: String(AdminId || ""),
  startTime: String(form.startTime || ""),
  endTime: String(form.endTime || ""),
  teamId: String(form.teamId || ""),
  slotDurationMinutes: String(form.slotDurationMinutes || ""),
  maxDriverCapacity: String(form.maxDriverCapacity || ""),
  isActive: Number(form.isActive) === 1 ? 1 : 0,
};

    if (!payload.adminId) {
      toast.error("Admin ID is required");
      return false;
    }

    if (!payload.startTime) {
      toast.error("Start time is required");
      return false;
    }

    if (!payload.endTime) {
      toast.error("End time is required");
      return false;
    }

    if (!payload.teamId) {
      toast.error("Team is required");
      return false;
    }

    if (!payload.slotDurationMinutes) {
      toast.error("Slot duration is required");
      return false;
    }

    if (!payload.maxDriverCapacity) {
      toast.error("Maximum driver capacity is required");
      return false;
    }

    try {
      setLoading?.(true);

      console.log(
        "INSERT TIME WINDOW CONFIG PAYLOAD:",
        payload
      );

      const res = await api.post(
        "/api/Slot/v1.0/insertTimeWindowConfig",
        payload,
      );

      if (res.data?.responseCode === "00") {
        toast.success(
          res.data?.responseMessage ||
            "Time window configuration created successfully",
        );

        if (typeof onSuccess === "function") {
          await onSuccess(res.data);
        }

        return true;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage ||
            "Failed to create time window configuration",
        );
      }

      return false;
    } catch (error) {
      console.error(
        "insertTimeWindowConfigApi error:",
        error
      );

      toast.error(
        error?.response?.data?.responseMessage ||
          "Error creating time window configuration",
      );

      return false;
    } finally {
      setLoading?.(false);
    }
  };


  // ---------- GET ALL TIME WINDOW CONFIG ----------

  const fetchAllTimeWindowConfigApi = async (
    userId = "",
    setData,
    setLoading,
  ) => {
    setLoading?.(true);

    try {
      const payload = {
        userId: String(userId || ""),
      };

      const res = await api.post(
        "/api/Slot/v3.0/getAllTimeWindowConfig",
        payload,
      );

      if (res.data?.responseCode === "00") {
        const list = getResponseData(res.data);

        setData?.(list);

        return list;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(
          res.data?.responseMessage ||
            "Failed to fetch time window configurations",
        );
      }

      setData?.([]);

      return [];
    } catch (error) {
      console.error(
        "fetchAllTimeWindowConfigApi error:",
        error
      );

      toast.error(
        error?.response?.data?.responseMessage ||
          "Error fetching time window configurations",
      );

      setData?.([]);

      return [];
    } finally {
      setLoading?.(false);
    }
  };

  return {
    fetchSlotsApi,
    createSlotApi,
    deleteSlotApi,
    updateDriverSlotStatusApi,
    fetchBookedDriversApi,
    createSlotApiV2,
    fetchSlotsApiV2,
    assignDriverToSlotApiV2,
    insertTimeWindowConfigApi,
    fetchAllTimeWindowConfigApi,
  };
}
